import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jerseyApi, Pattern } from '../../api/jerseyApi';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { PatternFilters, PatternActionData } from '../../types/pattern';
import PatternCard from './PatternCard';
import PatternForm from './PatternForm';
import PatternUpload from './PatternUpload';

const PatternLibrary: React.FC = () => {
  const [filters, setFilters] = useState<PatternFilters>({
    search: '',
    element_type_id: null,
    status: null,
  });
  const [selectedAction, setSelectedAction] = useState<PatternActionData | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(8);

  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  // Fetch patterns with pagination
  const { data: patternsResponse, isLoading: patternsLoading, error: patternsError } = useQuery({
    queryKey: ['patterns', filters, currentPage, pageSize],
    queryFn: () => jerseyApi.getPatterns({
      page: currentPage,
      size: pageSize,
      search: filters.search || undefined,
      element_type_id: filters.element_type_id || undefined,
      status: filters.status ?? undefined,
      include_svg: true, // Ensure SVG content is returned for previews
    }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const patterns = patternsResponse?.items || [];
  patterns.forEach((pattern: Pattern) => {
    console.log(`Pattern slug: ${pattern.slug}, status: ${pattern.status}`);
  });
  const totalPatterns = patternsResponse?.total || 0;
  const totalPages = patternsResponse?.pages || 0;

  // Fetch element types for filtering
  const { data: elementTypes = [] } = useQuery({
    queryKey: ['element-types'],
    queryFn: () => jerseyApi.getElementTypes(),
  });

  // Create pattern mutation
  const createPatternMutation = useMutation({
    mutationFn: jerseyApi.createPattern,
    onSuccess: () => {
      showSnackbar('Pattern created successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['patterns'] });
      setSelectedAction(null);
    },
    onError: (error: any) => {
      showSnackbar(
        error.response?.data?.detail || 'Failed to create pattern',
        'error'
      );
    },
  });

  // Update pattern mutation
  const updatePatternMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      jerseyApi.updatePattern(id, data),
    onSuccess: () => {
      showSnackbar('Pattern updated successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['patterns'] });
      setSelectedAction(null);
    },
    onError: (error: any) => {
      showSnackbar(
        error.response?.data?.detail || 'Failed to update pattern',
        'error'
      );
    },
  });

  // Delete pattern mutation
  const deletePatternMutation = useMutation({
    mutationFn: jerseyApi.deletePattern,
    onSuccess: () => {
      showSnackbar('Pattern deleted successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['patterns'] });
      setSelectedAction(null);
    },
    onError: (error: any) => {
      showSnackbar(
        error.response?.data?.detail || 'Failed to delete pattern',
        'error'
      );
    },
  });

  // Upload SVG mutation
  const uploadSvgMutation = useMutation({
    mutationFn: ({ id, view, file }: { id: number; view: 'front' | 'side'; file: File }) =>
      jerseyApi.uploadPatternSVG(id, view, file),
    onSuccess: () => {
      showSnackbar('SVG uploaded successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['patterns'] });
      setSelectedAction(null);
    },
    onError: (error: any) => {
      showSnackbar(
        error.response?.data?.detail || 'Failed to upload SVG',
        'error'
      );
    },
  });

  const handleFilterChange = (field: keyof PatternFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      element_type_id: null,
      status: null,
    });
    setCurrentPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handlePatternAction = (action: PatternActionData) => {
    setSelectedAction(action);
  };

  const handleFormSubmit = async (data: any) => {
    if (selectedAction?.pattern) {
      // Update existing pattern
      updatePatternMutation.mutate({
        id: selectedAction.pattern.id,
        data: {
          description: data.description,
          element_type_id: data.element_type_id,
          matching_string: data.matching_string,
          absolute_positioning: data.absolute_positioning ?? null,
          absolute_positioning_front: data.absolute_positioning_front ?? null,
          absolute_positioning_side: data.absolute_positioning_side ?? null,
          is_logo: !!data.is_logo,
          status: data.status,
        },
      });
    } else {
      // Create new pattern
      try {
        // First create the pattern without SVG files
        const patternData = {
          description: data.description,
          element_type_id: data.element_type_id,
          matching_string: data.matching_string,
          absolute_positioning: data.absolute_positioning ?? null,
          absolute_positioning_front: data.absolute_positioning_front ?? null,
          absolute_positioning_side: data.absolute_positioning_side ?? null,
          is_logo: !!data.is_logo,
          status: data.status,
        };

        const newPattern = await jerseyApi.createPattern(patternData);

        // Then upload SVG files if provided
        const uploadPromises = [];

        if (data.front_svg_file) {
          uploadPromises.push(
            jerseyApi.uploadPatternSVG(newPattern.id, 'front', data.front_svg_file)
          );
        }

        if (data.side_svg_file) {
          uploadPromises.push(
            jerseyApi.uploadPatternSVG(newPattern.id, 'side', data.side_svg_file)
          );
        }

        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          await Promise.all(uploadPromises);
        }

        showSnackbar('Pattern created successfully with SVG files', 'success');
        queryClient.invalidateQueries({ queryKey: ['patterns'] });
        setSelectedAction(null);
      } catch (error) {
        console.error('Error creating pattern with SVG files:', error);
        showSnackbar('Error creating pattern. Please try again.', 'error');
      }
    }
  };

  const handleUploadSubmit = (file: File) => {
    if (selectedAction?.pattern && selectedAction?.view) {
      uploadSvgMutation.mutate({
        id: selectedAction.pattern.id,
        view: selectedAction.view,
        file,
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedAction?.pattern) {
      deletePatternMutation.mutate(selectedAction.pattern.id);
    }
  };

  const handleToggleStatus = (pattern: Pattern) => {
    updatePatternMutation.mutate({
      id: pattern.id,
      data: { status: !pattern.status },
    });
  };

  const activePatterns = patterns.filter(p => p.status);
  const inactivePatterns = patterns.filter(p => !p.status);

  if (patternsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load patterns. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Pattern Library
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => queryClient.invalidateQueries({ queryKey: ['patterns'] })}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Toggle Filters">
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handlePatternAction({ action: 'create' })}
          >
            Create Pattern
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Search patterns"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Element Type</InputLabel>
                  <Select
                    value={filters.element_type_id || ''}
                    onChange={(e) => handleFilterChange('element_type_id', e.target.value || null)}
                    label="Element Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {elementTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status === null ? '' : filters.status.toString()}
                    onChange={(e) => handleFilterChange('status', e.target.value === '' ? null : e.target.value === 'true')}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClearFilters}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Patterns</Typography>
              <Typography variant="h4" color="primary">
                {totalPatterns}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Current Page</Typography>
              <Typography variant="h4" color="info.main">
                {currentPage} / {totalPages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Active Patterns</Typography>
              <Typography variant="h4" color="success.main">
                {activePatterns.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Inactive Patterns</Typography>
              <Typography variant="h4" color="warning.main">
                {inactivePatterns.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Loading State */}
      {patternsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Pattern Grid */}
      {!patternsLoading && (
        <>
          <Grid container spacing={3}>
            {patterns.map((pattern: Pattern) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={pattern.id}>
                <PatternCard
                  pattern={pattern}
                  elementTypes={elementTypes}
                  onEdit={(pattern: Pattern) => handlePatternAction({ action: 'edit', pattern })}
                  onDelete={(pattern: Pattern) => handlePatternAction({ action: 'delete', pattern })}
                  onToggleStatus={handleToggleStatus}
                  onUpload={(pattern: Pattern, view: 'front' | 'side') => handlePatternAction({ action: 'upload', pattern, view })}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
              </FormControl>

              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />

              <Typography variant="body2" color="text.secondary">
                Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalPatterns)} of {totalPatterns}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Empty State */}
      {!patternsLoading && patterns.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No patterns found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {filters.search || filters.element_type_id || filters.status !== null
              ? 'Try adjusting your filters or search terms.'
              : 'Get started by creating your first pattern.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handlePatternAction({ action: 'create' })}
          >
            Create First Pattern
          </Button>
        </Box>
      )}

      {/* Dialogs */}
      {/* Create/Edit Pattern Dialog */}
      <Dialog
        open={selectedAction?.action === 'create' || selectedAction?.action === 'edit'}
        onClose={() => setSelectedAction(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedAction?.action === 'create' ? 'Create New Pattern' : 'Edit Pattern'}
        </DialogTitle>
        <DialogContent>
          <PatternForm
            pattern={selectedAction?.pattern}
            elementTypes={elementTypes}
            onSubmit={handleFormSubmit}
            onCancel={() => setSelectedAction(null)}
            loading={createPatternMutation.isPending || updatePatternMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Upload SVG Dialog */}
      <Dialog
        open={selectedAction?.action === 'upload'}
        onClose={() => setSelectedAction(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload {selectedAction?.view} SVG for {selectedAction?.pattern?.description}
        </DialogTitle>
        <DialogContent>
          {selectedAction?.pattern && selectedAction?.view && (
            <PatternUpload
              pattern={selectedAction.pattern}
              view={selectedAction.view}
              onUpload={handleUploadSubmit}
              onCancel={() => setSelectedAction(null)}
              loading={uploadSvgMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={selectedAction?.action === 'delete'}
        onClose={() => setSelectedAction(null)}
      >
        <DialogTitle>Delete Pattern</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the pattern "{selectedAction?.pattern?.description}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAction(null)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletePatternMutation.isPending}
          >
            {deletePatternMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatternLibrary;
